/**
 * The farm's voice.
 *
 * Spec §8 names these explicitly: "Only machinery: extractor fans, a slurry pump,
 * a distant conveyor, the hall's 40 Hz hum." These are that text, implemented —
 * reference material for how a machine bed is built, and the workbench's
 * self-test. They are shippable sounds, not placeholders, but expect them to be
 * re-voiced once there is a room to hear them in.
 *
 * Design note that applies to every bed in this game: **the farm never falls
 * silent, so the beds must be boring.** Anything with a memorable shape becomes
 * exhausting over ninety minutes. Interest comes from layering and from what is
 * ducked out, never from the bed itself.
 */

import { noiseSource, filter, gain, lfo, lfoOut, chain, connectChain, softClip } from '../synth';
import type { SoundDefinition } from '../types';

/**
 * The fattening hall's 40 Hz hum — spec §8, named exactly.
 *
 * The 40 Hz fundamental remains dominant, with restrained second and third
 * harmonics so the hall is still present on small speakers. A very slight detune
 * between the fundamentals keeps it from phasing into perfect stillness.
 */
const hallHum: SoundDefinition = {
  id: 'hall-hum-40',
  label: 'Fattening hall — 40 Hz hum',
  description: "The hall's fundamental. Present in every frame of the fattening hall.",
  category: 'machine',
  duration: 4,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.5);
    out.connect(ctx.destination);

    // The harmonic series reaches to 320 Hz, and that is the entire point.
    //
    // This sound has now been rejected TWICE for being inaudible on real
    // speakers. The first fix added 80 and 120 Hz, which did nothing, and the
    // reason is physics rather than mixing: phone and laptop drivers have
    // essentially no output below ~400 Hz, so every partial we had was beneath
    // the floor of the devices that were the problem. Turning it up would only
    // have moved inaudible energy around.
    //
    // So the fundamental is IMPLIED. Every frequency here is a multiple of 40 Hz,
    // which means a full-range system fuses them back into one deep tone — the
    // reading Alex confirmed was correct on headphones and which must not change
    // — while a phone reproduces the upper members and hears the same pitch by
    // inference. This is how a bass guitar survives a laptop speaker.
    //
    // If this is ever retuned, keep `hall hum carries above 300 Hz` in
    // audio.spec.ts green. It exists so the failure cannot recur silently.
    const partials: readonly [number, number][] = [
      [40, 0.16],
      [80, 0.06],
      [120, 0.03],
      // The 40.25 Hz beat against the fundamental. Slow, and the only movement
      // in the sound — the beds must be boring.
      [40.25, 0.06],
      // Reaching to 480 Hz, and the top of this series is not decoration.
      //
      // A first attempt stopped at 320 and measured 4.6 % of total energy above
      // 300 Hz — still essentially nothing where a phone can hear. The partials
      // BELOW 300 Hz do not help with the problem being solved, however sensible
      // they look in a list. Every one of these is a multiple of 40, so they fuse
      // into the fundamental on a system that can reproduce it.
      [200, 0.030],
      [240, 0.026],
      [280, 0.024],
      [320, 0.022],
      [360, 0.020],
      [400, 0.018],
      [440, 0.014],
      [480, 0.011],
    ];

    for (const [freq, level] of partials) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      connectChain(osc, gain(ctx, level), out);
      osc.start(0);
    }

    // A breath of filtered noise so it sits in a room rather than in a vacuum.
    connectChain(
      noiseSource(ctx, 4, 11),
      filter(ctx, { type: 'lowpass', frequency: 220, q: 0.7 }),
      gain(ctx, 0.02),
      out,
    );

    // Air in a large concrete room, band-limited to where small speakers live.
    // Quiet enough to be unnoticeable on a good system and to stay the sound of
    // a big empty building rather than of hiss.
    connectChain(
      noiseSource(ctx, 4, 29),
      filter(ctx, { type: 'highpass', frequency: 300, q: 0.6 }),
      filter(ctx, { type: 'lowpass', frequency: 1200, q: 0.6 }),
      gain(ctx, 0.022),
      out,
    );
  },
};

/**
 * Extractor fan — spec §8.
 *
 * Broadband noise shaped by a resonant bandpass, with blade-rate amplitude
 * modulation. The wobble is what makes it read as rotating machinery rather than
 * as hiss.
 */
const extractorFan: SoundDefinition = {
  id: 'extractor-fan',
  label: 'Extractor fan',
  description: 'Wall-mounted extractor. Blade wobble at ~7 Hz over a bandpassed bed.',
  category: 'machine',
  duration: 4,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.6);
    out.connect(ctx.destination);

    const body = chain(
      noiseSource(ctx, 4, 23),
      filter(ctx, { type: 'bandpass', frequency: 420, q: 1.2 }),
      filter(ctx, { type: 'lowpass', frequency: 2600, q: 0.7 }),
      gain(ctx, 0.16),
    );

    // Blade-rate modulation.
    const wobble = lfo(ctx, 7.1, 0.35);
    const modulated = gain(ctx, 0.75);
    lfoOut(wobble).connect(modulated.gain);
    body.connect(modulated).connect(out);

    // Motor whine, well below the noise so it is felt more than heard.
    const whine = ctx.createOscillator();
    whine.type = 'triangle';
    whine.frequency.value = 213;
    whine.connect(gain(ctx, 0.012)).connect(out);
    whine.start(0);
  },
};

/**
 * Fluorescent tube buzz — the farrowing pens.
 *
 * Mains hum at 100 Hz (European 50 Hz doubled by the ballast) plus a restrained
 * upper harmonic. Deliberately oppressive: this is the sound of the room Rosalie
 * is born in.
 */
const fluorescentBuzz: SoundDefinition = {
  id: 'fluorescent-buzz',
  label: 'Fluorescent tube buzz',
  description: 'Ballast hum at 100 Hz with a rolled-off upper harmonic. Farrowing pens.',
  category: 'machine',
  // 4 s so the flicker can complete a whole number of cycles — a loop whose
  // modulator does not land on its period clicks every time it wraps.
  duration: 4,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.35);

    // Flicker sits between the bed and the output, so it actually modulates.
    const flickerGain = gain(ctx, 0.8);
    const flicker = lfo(ctx, 0.75, 0.18, 'square'); // 3 whole cycles in 4 s
    lfoOut(flicker).connect(flickerGain.gain);

    connectChain(out, flickerGain, softClip(ctx, 1.2), ctx.destination);

    for (const [freq, level, type] of [
      [100, 0.09, 'sawtooth'],
      [200, 0.03, 'square'],
      [4000, 0.0035, 'sine'],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = freq;
      // connectChain, not chain — see the warning in synth.ts. Written the other
      // way, the gain stage drops out of the path and this clips hard.
      connectChain(
        osc,
        gain(ctx, level),
        filter(ctx, { type: 'lowpass', frequency: 2800, q: 0.7 }),
        out,
      );
      osc.start(0);
    }
  },
};

/**
 * Slurry pump — spec §8.
 *
 * A slow reciprocating thump under a wet, filtered bed. The rhythm is the point:
 * it gives the corridors a pulse to move against, and the stealth system can hide
 * footfalls under its downbeat.
 */
const slurryPump: SoundDefinition = {
  id: 'slurry-pump',
  label: 'Slurry pump',
  description: 'Reciprocating thump at ~0.8 Hz under a wet low bed. Gives corridors a pulse.',
  category: 'machine',
  duration: 5,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.6);
    out.connect(ctx.destination);

    const bed = chain(
      noiseSource(ctx, 5, 37),
      filter(ctx, { type: 'lowpass', frequency: 340, q: 0.9 }),
      gain(ctx, 0.05),
    );
    bed.connect(out);

    // Four thumps across the 5 s loop = 0.8 Hz.
    const thump = gain(ctx, 0);
    const thumpOsc = ctx.createOscillator();
    thumpOsc.type = 'sine';
    thumpOsc.frequency.value = 58;
    thumpOsc.connect(thump).connect(out);
    thumpOsc.start(0);

    const t0 = ctx.currentTime;
    for (let i = 0; i < 4; i++) {
      const t = t0 + i * 1.25;
      thump.gain.setValueAtTime(0, t);
      thump.gain.linearRampToValueAtTime(0.14, t + 0.02);
      thump.gain.exponentialRampToValueAtTime(0.001, t + 0.42);
    }
    // Land the loop exactly where it started.
    thump.gain.setValueAtTime(0, t0 + 5);
  },
};

export const machinerySounds: readonly SoundDefinition[] = [
  hallHum,
  extractorFan,
  fluorescentBuzz,
  slurryPump,
];
